const UBUNIFU_LOGO_URL = 'https://ubunifucollege.com/assets/images/logo-main-black.png';
const STUDENT_DASHBOARD_URL = 'https://live.ubunifucollege.com/login';
const STUDENT_PASSWORD_RESET_URL = 'https://live.ubunifucollege.com/recover';
const INVOICE_VIEW_BASE_URL = 'https://live.ubunifucollege.com/view-invoice?';
const ADMISSIONS_EMAIL = 'admissions@ubunifucollege.com';
const ADMISSIONS_PHONE = '+254 727 634 101';
const FEE_PAYMENT_PAYBILL = '4052081';

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const escapeAttribute = (value) => escapeHtml(value).replace(/`/g, '&#096;');

const getDateObject = (value) => {
  if (!value) return null;

  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj;
};

const formatDate = (value, fallback = 'Not set') => {
  const dateObj = getDateObject(value);
  if (!dateObj) return fallback;

  return dateObj.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatDateTime = (value, fallback = 'Not set') => {
  const dateObj = getDateObject(value);
  if (!dateObj) return fallback;

  return dateObj.toLocaleString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Africa/Nairobi',
    timeZoneName: 'short'
  });
};

const formatKes = (value) => {
  const numericAmount = Number(value || 0);
  if (!Number.isFinite(numericAmount)) return 'KES 0';

  return `KES ${numericAmount.toLocaleString('en-KE', {
    maximumFractionDigits: 0
  })}`;
};

const formatAmount = (value, currency = 'KES') => {
  const rawValue = String(value ?? '').trim();
  const safeCurrency = String(currency || 'KES').toUpperCase() === 'USD' ? 'USD' : 'KES';

  if (typeof value === 'string' && /[a-z]/i.test(rawValue)) {
    return rawValue;
  }

  const numericAmount = Number(value || 0);
  if (!Number.isFinite(numericAmount)) return `${safeCurrency} 0`;

  if (safeCurrency === 'USD') {
    return `USD ${numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  return formatKes(numericAmount);
};

const buildDetailsBlock = (details = []) => {
  const rows = details
    .filter((detail) => detail?.label)
    .filter((detail) => detail.value !== undefined && detail.value !== null && detail.value !== '')
    .map((detail) => `
      <strong>${escapeHtml(detail.label)}:</strong> ${escapeHtml(detail.value)}<br>
    `)
    .join('');

  if (!rows) return '';

  return `
    <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:14px;padding:18px 20px;margin-bottom:24px;">
      <p style="margin:0 0 12px;color:#475467;font-size:12px;font-weight:700;letter-spacing:1.3px;text-transform:uppercase;">Details</p>
      <p style="margin:0;color:#101828;font-size:15px;line-height:1.8;">
        ${rows}
      </p>
    </div>
  `;
};

const buildCta = ({ href, label, variant = 'primary' } = {}) => {
  if (!href || !label) return '';

  const style = variant === 'secondary'
    ? 'display:inline-block;background:#ffffff;color:#111827;text-decoration:none;padding:12px 18px;border-radius:999px;font-size:14px;font-weight:700;border:1px solid #d1d5db;margin-left:8px;margin-top:8px;'
    : 'display:inline-block;background:#111827;color:#ffffff;text-decoration:none;padding:12px 18px;border-radius:999px;font-size:14px;font-weight:700;margin-top:8px;';

  return `<a href="${escapeAttribute(href)}" target="_blank" style="${style}">${escapeHtml(label)}</a>`;
};

export const buildMinimalEmail = ({
  eyebrow,
  title,
  intro,
  details = [],
  bodyHtml = '',
  primaryCta,
  secondaryCta,
  footer = true
}) => {
  const ctas = [buildCta(primaryCta), buildCta(secondaryCta)].filter(Boolean).join('');

  return `
    <!doctype html>
    <html>
      <body style="margin:0;padding:0;background:#f4f6fb;font-family:Arial,Helvetica,sans-serif;color:#101828;">
        <div style="max-width:640px;margin:0 auto;padding:32px 12px;">
          <div style="background:#ffffff;border:1px solid #e6eaf2;border-radius:18px;overflow:hidden;">
            <div style="background:#111827;padding:30px;">
              <img src="${UBUNIFU_LOGO_URL}" style="height:38px;display:block;margin-bottom:28px;background:#ffffff;border-radius:999px;padding:8px;" alt="Ubunifu College">
              <p style="margin:0 0 12px;color:#a7f3d0;font-size:12px;font-weight:700;letter-spacing:1.6px;text-transform:uppercase;">${escapeHtml(eyebrow || 'Ubunifu College')}</p>
              <h1 style="margin:0;color:#ffffff;font-size:28px;line-height:1.2;">${escapeHtml(title || 'Update from Ubunifu College')}</h1>
              ${intro ? `<p style="margin:14px 0 0;color:#d1d5db;font-size:15px;line-height:1.7;">${escapeHtml(intro)}</p>` : ''}
            </div>

            <div style="padding:30px;">
              ${buildDetailsBlock(details)}
              ${bodyHtml}
              ${ctas ? `<div style="margin-top:28px;">${ctas}</div>` : ''}
            </div>

            ${footer ? `
              <div style="padding:22px 30px;background:#f8fafc;border-top:1px solid #e5e7eb;">
                <p style="margin:0;color:#667085;font-size:13px;line-height:1.6;">Ubunifu College<br>
                ${ADMISSIONS_EMAIL} | ${ADMISSIONS_PHONE}</p>
              </div>
            ` : ''}
          </div>
        </div>
      </body>
    </html>
  `;
};

export const buildInvoiceEmail = ({ studentName, amount, dueDate, invoiceNumber, note }) => {
  const invoiceUrl = `https://live.ubunifucollege.com/view-invoice?${encodeURIComponent(invoiceNumber)}`;

  return buildMinimalEmail({
    eyebrow: 'Student invoice',
    title: `Hi ${studentName || 'there'}, your invoice is ready.`,
    intro: note || 'Please review your invoice and complete payment by the due date.',
    details: [
      { label: 'Invoice', value: `#${invoiceNumber}` },
      { label: 'Amount', value: formatKes(amount) },
      { label: 'Due date', value: formatDate(dueDate) }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Open the invoice to view payment details and keep your learning plan on track.</p>
    `,
    primaryCta: { href: invoiceUrl, label: 'View invoice' },
    secondaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Student login', variant: 'secondary' }
  });
};

export const buildReceiptEmail = ({ studentName, amount, paymentDate, paymentMethod, receiptNumber }) => {
  const receiptUrl = `https://live.ubunifucollege.com/view-receipt?${encodeURIComponent(receiptNumber)}`;

  return buildMinimalEmail({
    eyebrow: 'Payment receipt',
    title: `Thank you, ${studentName || 'there'}. Your payment has been received.`,
    intro: 'We have recorded your payment successfully. Keep this receipt for your records.',
    details: [
      { label: 'Receipt', value: `#${receiptNumber}` },
      { label: 'Amount paid', value: formatKes(amount) },
      { label: 'Payment date', value: formatDate(paymentDate) },
      { label: 'Payment method', value: paymentMethod || 'Not specified' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">You can view the receipt online any time using the button below.</p>
    `,
    primaryCta: { href: receiptUrl, label: 'View receipt' },
    secondaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Student login', variant: 'secondary' }
  });
};

export const buildFeeReminderEmail = ({ studentName, amount, dueDate, invoiceNumber, message }) => {
  const invoiceUrl = `https://live.ubunifucollege.com/view-invoice?${encodeURIComponent(invoiceNumber)}`;

  return buildMinimalEmail({
    eyebrow: 'Fee reminder',
    title: `Hi ${studentName || 'there'}, your fee payment needs attention.`,
    intro: message || 'Please review your invoice and complete your payment.',
    details: [
      { label: 'Invoice', value: `#${invoiceNumber}` },
      { label: 'Amount due', value: formatKes(amount) },
      { label: 'Due date', value: formatDate(dueDate) }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">If you have already paid, please ignore this reminder or contact finance so we can update your account.</p>
    `,
    primaryCta: { href: invoiceUrl, label: 'View invoice' },
    secondaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Student login', variant: 'secondary' }
  });
};

export const buildEmployeeAccountEmail = ({ name, accountType, accountLink, email, password }) => {
  return buildMinimalEmail({
    eyebrow: 'Account created',
    title: `Hi ${name || 'there'}, your ${accountType || 'Ubunifu'} account is ready.`,
    intro: 'Your Ubunifu College staff account has been created successfully.',
    details: [
      { label: 'Account type', value: accountType || 'Staff' },
      { label: 'Login email', value: email },
      { label: 'Temporary password', value: password }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Please log in using the details above. For security, change your password after your first login if the platform allows it.</p>
    `,
    primaryCta: { href: accountLink, label: 'Open account' }
  });
};

export const buildAdmissionFeeReceiptEmail = ({
  studentName,
  course,
  amount,
  currency = 'KES',
  paymentReference,
  paymentMethod,
  paymentDate,
  prospectAdmissionNumber,
  nextStepText
}) => {
  return buildMinimalEmail({
    eyebrow: 'Admission fee received',
    title: `Hi ${studentName || 'there'}, your admission fee is confirmed.`,
    intro: `We have recorded your admission fee for ${course || 'your selected program'}.`,
    details: [
      { label: 'Amount', value: formatAmount(amount, currency) },
      { label: 'Payment method', value: paymentMethod },
      { label: 'Payment reference', value: paymentReference },
      { label: 'Payment date', value: formatDate(paymentDate) },
      { label: 'Application ref', value: prospectAdmissionNumber }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">${escapeHtml(nextStepText || 'Your admission fee step is now complete. Our admissions team will guide you on interview scheduling and the next enrollment step.')}</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};

export const buildAdmissionConfirmationEmail = ({
  studentName,
  admissionNumber,
  course,
  duration,
  admissionDate,
  classStartDate
}) => {
  return buildMinimalEmail({
    eyebrow: 'Admission confirmed',
    title: `Welcome to Ubunifu College, ${studentName || 'there'}.`,
    intro: `You have been admitted to ${course || 'your selected program'}. We are excited to have you join the Ubunifu community.`,
    details: [
      { label: 'Admission number', value: admissionNumber },
      { label: 'Program', value: course || 'Your selected program' },
      { label: 'Duration', value: duration || 'To be confirmed' },
      { label: 'Admission date', value: formatDate(admissionDate) },
      { label: 'Class start date', value: formatDate(classStartDate) }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Your next step is to log in to your student account and follow the onboarding instructions. This is where you will see your learning details, payment information, and updates from the admissions team.</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};

export const buildInterviewInvitationEmail = ({
  recipientName,
  studentName,
  adminName,
  scheduledStart,
  scheduledEnd,
  meetingLink,
  googleCalendarLink,
  outlookCalendarLink,
  course,
  isAdminCopy = false
}) => {
  const intro = isAdminCopy
    ? `An interview invite has been sent for ${studentName || 'the applicant'}.`
    : `Your Ubunifu College admissions interview has been scheduled by ${adminName || 'our admissions team'}.`;
  const studentAccessMessage = `
      <p style="margin:0 0 14px;color:#667085;font-size:14px;line-height:1.7;">Open your interview from your student account using the link below. Sign in with the same email address and password you set during registration.</p>
      <p style="margin:0 0 14px;color:#667085;font-size:14px;line-height:1.7;">If you cannot remember your password, use <a href="${escapeAttribute(STUDENT_PASSWORD_RESET_URL)}" target="_blank" style="color:#111827;font-weight:700;text-decoration:none;">Reset Password</a> and we will send you a password reset link.</p>
  `;

  return buildMinimalEmail({
    eyebrow: 'Ubunifu College interview',
    title: isAdminCopy ? 'Interview scheduled.' : `Hi ${recipientName || studentName || 'there'}, your interview is scheduled.`,
    intro,
    details: [
      { label: 'Date and time', value: formatDateTime(scheduledStart) },
      { label: 'Expected end', value: formatDateTime(scheduledEnd) },
      { label: 'Course', value: course || 'Course not set' },
      { label: 'Interview link', value: meetingLink }
    ],
    bodyHtml: `
      <p style="margin:0 0 14px;color:#667085;font-size:14px;line-height:1.7;">Please join a few minutes before the start time so your camera and audio are ready.</p>
      ${isAdminCopy ? '' : studentAccessMessage}
      ${outlookCalendarLink ? `<p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Prefer Outlook? <a href="${escapeAttribute(outlookCalendarLink)}" target="_blank" style="color:#111827;font-weight:700;text-decoration:none;">Add this interview to Outlook Calendar</a>.</p>` : ''}
    `,
    primaryCta: { href: meetingLink, label: isAdminCopy ? 'Open interview link' : 'Open interview in student account' },
    secondaryCta: googleCalendarLink ? { href: googleCalendarLink, label: 'Add to Google Calendar', variant: 'secondary' } : undefined
  });
};

export const buildFirstPaymentInvoiceEmail = ({
  studentName,
  course,
  amount,
  currency = 'KES',
  dueDate,
  invoiceNumber,
  paybill = FEE_PAYMENT_PAYBILL,
  accountNumber = ''
}) => {
  const invoiceUrl = `${INVOICE_VIEW_BASE_URL}${encodeURIComponent(invoiceNumber)}`;
  const isUsdInvoice = String(currency || '').toUpperCase() === 'USD';
  const normalizedAccountNumber = String(accountNumber || '').trim();
  const paymentInstructions = isUsdInvoice
    ? 'Please log in to your student account and complete the payment from your dashboard. International payments are handled inside the student account.'
    : `You can pay from your student account or via M-Pesa Paybill ${paybill}, Account ${normalizedAccountNumber || 'Application Ref pending'}.`;

  return buildMinimalEmail({
    eyebrow: 'First payment invoice',
    title: `Hi ${studentName || 'there'}, your first fee invoice is ready.`,
    intro: `Following your admissions interview, you can now complete the first payment step for ${course || 'your selected program'}.`,
    details: [
      { label: 'Invoice', value: `#${invoiceNumber}` },
      { label: 'Amount', value: formatAmount(amount, currency) },
      { label: 'Due date', value: formatDate(dueDate) },
      { label: 'Course', value: course || 'Your selected program' }
    ],
    bodyHtml: `
      <div style="background:#f8fafc;border:1px solid #e5e7eb;border-radius:14px;padding:18px 20px;margin:0 0 24px;">
        <p style="margin:0 0 12px;color:#475467;font-size:12px;font-weight:700;letter-spacing:1.3px;text-transform:uppercase;">How to pay</p>
        <p style="margin:0;color:#101828;font-size:15px;line-height:1.8;">${escapeHtml(paymentInstructions)}</p>
      </div>
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Please open the invoice or log in to your student account to complete payment. Once payment is confirmed, our team will guide you through the final onboarding steps.</p>
    `,
    primaryCta: { href: invoiceUrl, label: 'View invoice' },
    secondaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Student login', variant: 'secondary' }
  });
};

export const buildSecondaryCourseEmail = ({ studentName, courseName, intakeLabel }) => {
  return buildMinimalEmail({
    eyebrow: 'Course added',
    title: `Hi ${studentName || 'there'}, your secondary course has been added.`,
    intro: 'Please review the course details and complete the invoice step to activate this intake.',
    details: [
      { label: 'Course', value: courseName || 'Course' },
      { label: 'Intake', value: intakeLabel || 'Not set' }
    ],
    bodyHtml: `
      <p style="margin:0;color:#667085;font-size:14px;line-height:1.7;">Go to your invoices page when you are ready to complete payment and finalize admission for this course.</p>
    `,
    primaryCta: { href: STUDENT_DASHBOARD_URL, label: 'Open student account' }
  });
};
